import { DateTime } from 'luxon'

/*
  Monta a DRE a partir de /businesses/:id/earnings_by_type (receitas), /dre_costs (CMV
  e faixas do Simples) e dos lançamentos do próprio salão (despesas,
  comissões e bônus).

  Regras de negócio:
   1. Receita de Serviços = Agendamentos na comanda + Serviços avulsos na comanda +
      Agendamentos Cutilei. Serviço debitado de pacote ou de cartão fidelidade já não
      entra nesses totais — a API os exclui, pois o salão faturou na venda do pacote
      (mesma regra de services/Comission.js).
   2. Descontos = diferença entre o preço de tabela e o valor cobrado, somada por
      origem (agendamentos, serviços avulsos, produtos, pacotes). Agendamento Cutilei
      não gera desconto: o preço já é o negociado do plano, sem preço de tabela do
      salão para comparar.
   3. RECEITA LÍQUIDA = RECEITA TOTAL - Descontos - CMV de produtos.
   4. Análise vertical de cada linha usa como base a RECEITA LÍQUIDA do ano — igual à
      planilha de referência (coluna "Analise Vertical", onde a base é sempre 1).
   5. Custos variáveis = despesas lançadas como variáveis + comissões dos profissionais +
      bônus (créditos - descontos dos profissionais) + taxas de maquininha (soma do
      valor da taxa gravado em cada pagamento de cartão da comanda; vem de
      /dre_costs, não é lançada à mão). Custos fixos = despesas lançadas
      como fixas. DESPESAS TOTAIS = fixos + variáveis.
   6. Impostos = alíquota efetiva do Simples x RECEITA TOTAL do mês. A alíquota efetiva é
      (RBT12 x alíquota - dedução) / RBT12, com RBT12 = receita bruta dos 12 meses
      anteriores. Com menos de 12 meses de receita no período, o RBT12 é a média mensal
      dos meses com receita x 12; sem nenhum, a receita do próprio mês x 12.
   7. LUCRO LÍQUIDO = RECEITA LÍQUIDA - (custos fixos + custos variáveis + impostos).

  Custos entram nas linhas com sinal negativo, como a linha de Descontos.

  Portado de cutilei-business-app/src/services/Dre.js — mesma matemática, mantida em
  sincronia por ser puro cálculo (sem HTTP), igual a Comission.js neste repo.
*/
export default class Dre {
  static getMonthKeys(year) {
    return Array.from({ length: 12 }, (_, index) => (
      DateTime.local(Number(year), index + 1, 1).setLocale('pt-BR').toFormat('MMM yyyy')
    ))
  }

  // Receita bruta (serviços + produtos + pacotes) de cada mês do ano, na mesma regra
  // de buildStatement. Serve de histórico para o RBT12 do ano seguinte.
  static getMonthlyRevenue({ earningsByType = {}, earningsByBillItem = {} } = {}, year) {
    return this.getMonthKeys(year).map(key => (
      (earningsByType[key]?.cutilei?.totalValue ?? 0) +
      ['schedules', 'services', 'products', 'bundles'].reduce((total, group) => (
        total + (earningsByBillItem[key]?.[group]?.totalValue ?? 0)
      ), 0)
    ))
  }

  // Chave "MMM yyyy" (a mesma da API) de uma data ISO (YYYY-MM-DD).
  static getMonthKeyFromDate(date) {
    return DateTime.fromISO(String(date).slice(0, 10)).setLocale('pt-BR').toFormat('MMM yyyy')
  }

  /*
    Alíquota efetiva do Simples para um RBT12. `brackets` vem ordenado por teto (upTo);
    acima do último teto vale a última faixa.
  */
  static getEffectiveTaxRate(rbt12, brackets = []) {
    if (!brackets.length || rbt12 <= 0) return 0

    const bracket = brackets.find(item => rbt12 <= item.upTo) ?? brackets[brackets.length - 1]

    return Math.max((rbt12 * bracket.rate - bracket.deduction) / rbt12, 0)
  }

  /*
    Imposto de cada mês. `previousYearRevenue` são as 12 receitas brutas do ano anterior
    (ou null se não houver), que completam a janela de 12 meses de janeiro em diante.
  */
  static getMonthlyTaxes(revenueTotal, previousYearRevenue, brackets) {
    const history = [...(previousYearRevenue ?? Array.from({ length: 12 }, () => 0)), ...revenueTotal]

    return revenueTotal.map((revenue, index) => {
      const window = history.slice(index, index + 12)
      const monthsWithRevenue = window.filter(value => value > 0).length
      const windowTotal = window.reduce((total, value) => total + value, 0)

      let rbt12 = windowTotal
      if (monthsWithRevenue === 0) rbt12 = revenue * 12
      else if (monthsWithRevenue < 12) rbt12 = (windowTotal / monthsWithRevenue) * 12

      return revenue * this.getEffectiveTaxRate(rbt12, brackets)
    })
  }

  static buildStatement(
    { earningsByType = {}, earningsByBillItem = {} } = {},
    year,
    {
      costs = {},
      expenses = [],
      commissions = {},
      bonuses = {},
      previousYearRevenue = null
    } = {}
  ) {
    const monthKeys = this.getMonthKeys(year)
    const sum = values => values.reduce((total, value) => total + value, 0)

    const monthlyValue = (key, group, field = 'totalValue') => (
      earningsByBillItem[key]?.[group]?.[field] ?? 0
    )

    const scheduledServices = monthKeys.map(key => monthlyValue(key, 'schedules'))
    const looseServices = monthKeys.map(key => monthlyValue(key, 'services'))
    const cutileiServices = monthKeys.map(key => earningsByType[key]?.cutilei?.totalValue ?? 0)
    const services = monthKeys.map((_, index) => (
      scheduledServices[index] + looseServices[index] + cutileiServices[index]
    ))

    const products = monthKeys.map(key => monthlyValue(key, 'products'))
    const bundles = monthKeys.map(key => monthlyValue(key, 'bundles'))

    const revenueTotal = monthKeys.map((_, index) => (
      services[index] + products[index] + bundles[index]
    ))

    const discounts = monthKeys.map(key => (
      monthlyValue(key, 'schedules', 'discountValue') +
      monthlyValue(key, 'services', 'discountValue') +
      monthlyValue(key, 'products', 'discountValue') +
      monthlyValue(key, 'bundles', 'discountValue')
    ))

    const cmv = monthKeys.map(key => costs.cmv?.[key] ?? 0)

    const netRevenue = monthKeys.map((_, index) => (
      revenueTotal[index] - discounts[index] - cmv[index]
    ))
    const netRevenueYearTotal = sum(netRevenue)

    // Despesas lançadas, agrupadas por tipo (fixa/variável) e categoria.
    const expenseGroups = { fixed: new Map(), variable: new Map() }

    expenses.forEach(expense => {
      const kind = expense.category?.kind === 'fixed' ? 'fixed' : 'variable'
      const expenseYear = String(expense.competence_month).slice(0, 4)
      if (expenseYear !== String(year)) return

      const monthIndex = Number(String(expense.competence_month).slice(5, 7)) - 1
      const categoryKey = expense.category?.id ?? expense.category?.name ?? 'sem-categoria'

      if (!expenseGroups[kind].has(categoryKey)) expenseGroups[kind].set(categoryKey, {
        name: expense.category?.name ?? 'Sem categoria',
        values: monthKeys.map(() => 0)
      })

      expenseGroups[kind].get(categoryKey).values[monthIndex] += Number(expense.value) || 0
    })

    const commissionValues = monthKeys.map(key => commissions[key] ?? 0)
    const bonusValues = monthKeys.map(key => bonuses[key] ?? 0)
    const cardFeeValues = monthKeys.map(key => costs.cardFees?.[key] ?? 0)

    const sumMonths = arrays => monthKeys.map((_, index) => sum(arrays.map(values => values[index])))

    const fixedCategories = [...expenseGroups.fixed.values()]
    const variableCategories = [...expenseGroups.variable.values()]

    const fixedCosts = sumMonths(fixedCategories.map(category => category.values))
    const variableCosts = sumMonths([
      ...variableCategories.map(category => category.values),
      commissionValues, bonusValues, cardFeeValues
    ])
    const totalExpenses = monthKeys.map((_, index) => fixedCosts[index] + variableCosts[index])

    const taxes = this.getMonthlyTaxes(revenueTotal, previousYearRevenue, costs.tax?.brackets ?? [])

    const netProfit = monthKeys.map((_, index) => (
      netRevenue[index] - totalExpenses[index] - taxes[index]
    ))

    const buildRow = (code, label, kind, values, key = label) => {
      const total = sum(values)

      return {
        key,
        code,
        label,
        kind,
        values,
        total,
        verticalAnalysis: netRevenueYearTotal === 0 ? 0 : total / netRevenueYearTotal
      }
    }

    const negative = values => values.map(value => -value)

    const discountPercentageRow = () => {
      const values = monthKeys.map((_, index) => (
        revenueTotal[index] === 0 ? 0 : -(discounts[index] / revenueTotal[index])
      ))
      const revenueYearTotal = sum(revenueTotal)

      return {
        key: '% de Descontos',
        code: null,
        label: '% de Descontos',
        kind: 'percentage',
        values,
        total: revenueYearTotal === 0 ? 0 : -(sum(discounts) / revenueYearTotal),
        verticalAnalysis: null
      }
    }

    const categoryRows = (kind, categories) => categories.map(category => (
      buildRow(null, category.name, 'child', negative(category.values), `${kind}-${category.name}`)
    ))

    return [
      buildRow('1-1', 'Receita de Serviços', 'group', services),
      buildRow(null, 'Agendamentos', 'child', scheduledServices),
      buildRow(null, 'Serviços avulsos', 'child', looseServices),
      buildRow(null, 'Cutilei', 'child', cutileiServices),
      buildRow('1-2', 'Receita de Produtos', 'group', products),
      buildRow('1-3', 'Receita de Pacotes', 'group', bundles),
      buildRow(null, 'RECEITA TOTAL', 'total', revenueTotal),
      buildRow('1-4', 'Descontos', 'group', negative(discounts)),
      discountPercentageRow(),
      buildRow('2-1', 'CMV Produtos', 'group', negative(cmv)),
      buildRow(null, 'RECEITA LÍQUIDA', 'total', netRevenue),
      buildRow('3-1', 'Custos Fixos', 'group', negative(fixedCosts)),
      ...categoryRows('fixed', fixedCategories),
      buildRow('3-2', 'Custos Variáveis', 'group', negative(variableCosts)),
      buildRow(null, 'Comissões', 'child', negative(commissionValues)),
      buildRow(null, 'Bônus', 'child', negative(bonusValues)),
      buildRow(null, 'Taxas de maquininha', 'child', negative(cardFeeValues)),
      ...categoryRows('variable', variableCategories),
      buildRow(null, 'DESPESAS TOTAIS', 'total', negative(totalExpenses)),
      buildRow('4-1', 'Impostos', 'group', negative(taxes)),
      buildRow(null, 'LUCRO LÍQUIDO', 'total', netProfit)
    ]
  }

  /*
    Deriva as séries dos gráficos a partir das linhas de buildStatement (localizadas
    pelo rótulo), sem recalcular nada — assim tabela e gráfico nunca divergem.
    Descontos e custos saem em valor absoluto: na tabela a linha é negativa, mas nos
    gráficos e cards o custo é uma magnitude.
  */
  static buildChartData(statement = [], monthLabels = []) {
    const findRow = label => statement.find(row => row.label === label)
    const valuesOf = label => findRow(label)?.values ?? monthLabels.map(() => 0)
    const totalOf = label => findRow(label)?.total ?? 0

    const services = valuesOf('Receita de Serviços')
    const products = valuesOf('Receita de Produtos')
    const bundles = valuesOf('Receita de Pacotes')
    const revenueTotal = valuesOf('RECEITA TOTAL')
    const netRevenue = valuesOf('RECEITA LÍQUIDA')
    const netProfit = valuesOf('LUCRO LÍQUIDO')

    const summary = {
      revenueTotal: totalOf('RECEITA TOTAL'),
      discounts: Math.abs(totalOf('Descontos')),
      discountPercentage: Math.abs(totalOf('% de Descontos')),
      netRevenue: totalOf('RECEITA LÍQUIDA'),
      expenses: Math.abs(totalOf('DESPESAS TOTAIS')),
      taxes: Math.abs(totalOf('Impostos')),
      netProfit: totalOf('LUCRO LÍQUIDO')
    }

    return {
      monthlyComposition: [
        ['Mês', 'Serviços', 'Produtos', 'Pacotes'],
        ...monthLabels.map((label, index) => [
          label, services[index], products[index], bundles[index]
        ])
      ],
      monthlyRevenue: [
        ['Mês', 'Receita Total', 'Receita Líquida'],
        ...monthLabels.map((label, index) => [label, revenueTotal[index], netRevenue[index]])
      ],
      monthlyProfit: [
        ['Mês', 'Receita Líquida', 'Lucro Líquido'],
        ...monthLabels.map((label, index) => [label, netRevenue[index], netProfit[index]])
      ],
      yearComposition: [
        ['Origem', 'Valor'],
        ['Agendamentos', totalOf('Agendamentos')],
        ['Serviços avulsos', totalOf('Serviços avulsos')],
        ['Cutilei', totalOf('Cutilei')],
        ['Produtos', totalOf('Receita de Produtos')],
        ['Pacotes', totalOf('Receita de Pacotes')]
      ],
      summary,
      hasRevenue: summary.revenueTotal !== 0
    }
  }
}
